

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import renderMathInElement from "katex/dist/contrib/auto-render";
import "katex/dist/katex.min.css";
import { versionFigures } from "@/lib/figures";
import { FigureLightbox, type ZoomedFigure } from "./figure-lightbox";
import { FigureChart, isInlineChart } from "./figure-chart";

interface MathContentProps {
  html: string;
  className?: string;
  style?: React.CSSProperties;
  /** When set, `{_}` placeholders render as a filled box containing this value. */
  blankValue?: string;
  /**
   * Make every figure tappable to open full screen. Off by default: in the
   * tutoring chat and the admin lists a figure is illustration beside other
   * controls, and a click target there competes with them. On where a child is
   * reading a figure to answer — see `markZoomableFigures`.
   */
  zoomableFigures?: boolean;
}

const ZOOM_HINT = "Bakstelėk, kad padidintum";

// Inline SVG rather than an emoji: 🔍 renders at a different size and colour on
// every platform, and this sits on top of a child's figure.
const MAGNIFIER =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" ' +
  'stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/>' +
  '<path d="m20 20-4.3-4.3M11 8v6M8 11h6"/></svg>';

/**
 * Marks each figure as openable and hangs a magnifier in its top-right corner.
 *
 * **The badge is measured onto the figure, never wrapped around it.** A wrapper
 * is the obvious way to position it and it changes layout: figure sizing here is
 * a stack of rules written against the `img` itself (three size tiers, the
 * option-row height, `.exercise-active`), and a shrink-wrapped wrapper turns
 * every left-aligned block figure into a centred one. So the badge is an
 * absolutely positioned sibling placed from the image's own box, and the image
 * keeps the exact layout it had before this feature existed.
 *
 * Positions are re-measured on load and on resize, because the first measurement
 * happens before the figure has been fetched and its box is still empty.
 *
 * Images inside a button are skipped — those are answer choices, where a tap
 * already means "pick this one" and must keep meaning that.
 */
function markZoomableFigures(root: HTMLElement): Array<[HTMLImageElement, HTMLElement]> {
  const placed: Array<[HTMLImageElement, HTMLElement]> = [];
  for (const img of Array.from(root.querySelectorAll("img"))) {
    if (img.closest("button")) continue;
    const host = img.parentElement;
    if (!host) continue;

    img.dataset.zoomable = "true";
    img.classList.add("figure-zoomable");
    img.setAttribute("role", "button");
    img.setAttribute("tabindex", "0");
    if (!img.title) img.title = ZOOM_HINT;

    // `position: relative` with no offsets moves nothing; it only makes this the
    // box the badge is positioned against.
    if (getComputedStyle(host).position === "static") host.style.position = "relative";

    const badge = document.createElement("span");
    badge.className = "figure-zoom__hint";
    badge.setAttribute("aria-hidden", "true");
    badge.innerHTML = MAGNIFIER;
    host.appendChild(badge);
    placed.push([img, badge]);
  }
  return placed;
}

function placeBadges(placed: Array<[HTMLImageElement, HTMLElement]>) {
  for (const [img, badge] of placed) {
    const host = badge.parentElement;
    if (!host) continue;
    const hostBox = host.getBoundingClientRect();
    const box = img.getBoundingClientRect();
    // A figure too small to carry a badge without covering half of it gets none:
    // an icon-sized picture is not the case this feature is for.
    if (box.width < 64 || box.height < 48) {
      badge.style.display = "none";
      continue;
    }
    badge.style.display = "";
    badge.style.left = `${box.right - hostBox.left}px`;
    badge.style.top = `${box.top - hostBox.top}px`;
  }
}

function figureFrom(img: HTMLImageElement): ZoomedFigure {
  return { src: img.getAttribute("src") || "", alt: img.getAttribute("alt") || "" };
}

/** One chart that is being drawn into the page instead of fetched as a picture. */
interface ChartSlot extends ZoomedFigure {
  host: HTMLElement;
  key: string;
}

/**
 * Takes the charts out of the markup and leaves a slot where each one stood.
 *
 * The `<img>` is removed rather than hidden, because the slot is what carries the
 * figure's size now and two boxes for one figure would leave a gap the height of
 * the chart. Everything else stays a picture: an `<img>` costs nothing, cannot
 * run anything, and is what every sizing rule in `globals.css` is written
 * against — see `isInlineChart` for what earns the exception.
 *
 * Images inside a button are left alone. Those are answer choices, where a tap
 * already means "pick this one", and a chart that answered a hover instead would
 * be competing with the question.
 */
function extractCharts(root: HTMLElement): ChartSlot[] {
  const slots: ChartSlot[] = [];
  for (const img of Array.from(root.querySelectorAll("img"))) {
    if (img.closest("button")) continue;
    const src = img.getAttribute("src") || "";
    if (!isInlineChart(src)) continue;
    const host = document.createElement("span");
    host.className = "figure-chart-slot";
    img.replaceWith(host);
    slots.push({ host, src, alt: img.getAttribute("alt") || "", key: `${slots.length}:${src}` });
  }
  return slots;
}

/**
 * Fill-in slots. Styled in globals.css rather than with Tailwind classes here,
 * because `.math-blank` (KaTeX's \square) already lived there and the two are
 * meant to look identical — kept apart, they drifted into a blue box and a grey
 * underline, and the plain one never got a dark-mode variant.
 *
 * `letter` narrows the slot to one character. See `.blank-letter`: a missing
 * LETTER and a missing WORD are not the same picture, and the word-sized slot
 * split „brai{_}yti" into three visual tokens.
 */
function blankSpan(letter: boolean, value?: string): string {
  const classes = ["blank", letter ? "blank-letter" : "", value ? "blank-filled" : ""]
    .filter(Boolean)
    .join(" ");
  return `<span class="${classes}">${value || "&nbsp;"}</span>`;
}

const CODE_SPAN =
  "rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.9em] dark:bg-slate-800 dark:text-slate-200";

/**
 * The generator writes markdown backticks into question text — most often around
 * math it was shown in backticks in its own prompt (`` `$9$` ``), occasionally
 * around a formula. Nothing here renders markdown, so those backticks reach the
 * child as literal characters sitting either side of the number.
 *
 * Math loses them outright; anything else keeps the intent as a code span.
 * Tags are stepped over so a backtick inside markup can't pair with one in text,
 * while `>` and `<` inside a span (`$45\square > 455$`) still survive.
 */
function replaceCodeSpans(html: string): string {
  return html
    .split(/(<[^>]*>)/g)
    .map((part, i) =>
      i % 2 === 1
        ? part
        : part.replace(/`([^`\n]+)`/g, (_full, inner: string) =>
            /^\$\$[\s\S]+\$\$$/.test(inner) || /^\$[^$]+\$$/.test(inner)
              ? inner
              : `<code class="${CODE_SPAN}">${inner}</code>`
          )
    )
    .join("");
}

// A real tag: `<` followed by a letter or `/`. Deliberately stricter than
// `<[^>]*>`, because this content uses `<` as a less-than sign — `$a < b$) yra
// **klaidingas**?<br>` would otherwise read as one long "tag" and the emphasis
// inside it would be skipped.
const HTML_TAG = /(<\/?[a-zA-Z][^>]*>)/;
const MATH_SPAN = /(\$\$[\s\S]+?\$\$|\$[^$]+?\$)/;

/**
 * Applies `fn` to the text a child reads as prose — never inside a `$…$` math
 * region, never inside a tag.
 *
 * Math is skipped for a sharper reason than tidiness: KaTeX auto-render runs
 * afterwards and pairs `$` delimiters *within a text node*, so inserting an
 * element in the middle of a math region splits it in two and the expression
 * stops rendering altogether.
 */
function outsideMathAndTags(html: string, fn: (text: string) => string): string {
  return html
    .split(MATH_SPAN)
    .map((chunk, mi) =>
      mi % 2 === 1
        ? chunk
        : chunk
            .split(HTML_TAG)
            .map((part, ti) => (ti % 2 === 1 ? part : fn(part)))
            .join("")
    )
    .join("");
}

/**
 * The generator writes markdown emphasis into question text — `**epitetas**`
 * around the term a question turns on, `*Mergaitės akys buvo gilios kaip jūra.*`
 * around a sentence being compared — and nothing here rendered markdown, so the
 * child read the asterisks as characters. Same defect as the backticks above,
 * and the same fix: honour the intent at render time rather than editing 1,600
 * questions.
 *
 * **Bold is unconditional; italic is not.** Measured over every question, answer,
 * hint and choice on disk, `**…**` occurs 1,603 times and is unambiguous — always
 * paired, never inside math, never spanning a line. A *lone* asterisk is a
 * different character in this content: it is the multiplication sign (`2 * 3 * 5`,
 * `1 * 1000000 + 4 * 100000`) and the blank marker (`{*}`): of the 98 single-`*`
 * pairs on disk, 60 are arithmetic and 3 are blanks. Pairing those would turn a
 * correct question into a wrong one, which is the most expensive defect here — a
 * child marked wrong for being right.
 *
 * So italic requires what markdown itself requires and this content cannot fake:
 * no whitespace just inside the delimiters (every arithmetic case has spaces
 * around its `*`), nothing belonging to math or a blank inside (`$ { } < >`), and
 * not glued to a word on the outside. Measured: 35 matches, all of them genuine
 * emphasis, no arithmetic.
 */
function replaceEmphasis(html: string): string {
  return outsideMathAndTags(html, (text) =>
    text
      .replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
      .replace(/(?<![*\w])\*(?![\s*])([^*\n$<>{}]*[^\s*$<>{}])\*(?![*\w])/g, "<em>$1</em>")
  );
}

/** A picked choice split across several blanks: „y, y" or „ų ... u" fills two. */
const CHOICE_PARTS = /\s*(?:,|;|\.\.\.|…)\s*/;
/**
 * What may stand inside a word: a letter or a few („ie", „žių") — or a prefix
 * or ending written with its hyphen („apy-", „-ais"), shown without it. Never
 * „taip" or „ne": those answer a question about the word, they are not part of it.
 */
const IN_WORD_VALUE = /^-?\p{L}{1,3}-?$/u;
const NOT_A_PIECE = new Set(["ne", "ar"]);

function inWordPiece(value: string): string | undefined {
  if (!IN_WORD_VALUE.test(value) || NOT_A_PIECE.has(value.toLowerCase())) return undefined;
  return value.replace(/^-|-$/g, "");
}

function replaceBlankTokens(html: string, blankValue?: string): string {
  const value = blankValue && blankValue.trim() ? blankValue.trim() : undefined;
  // Split on $...$ / $$...$$ math blocks; only substitute outside them
  // (inside math, `_` means subscript so `{_}` has a different meaning).
  const parts = html.split(/(\$\$[\s\S]+?\$\$|\$[^$]+?\$)/g);
  const blanks = parts.filter((_p, i) => i % 2 === 0).join("").split("{_}").length - 1;
  // A choice naming one answer per blank fills them in order. Otherwise it is
  // one answer — and inside a word only something that can be part of a word is
  // shown: picking „taip" on „Ar žodyje m{_}slė rašoma į?" must not print
  // „mtaipslė", and a whole-word choice in a letter slot reads the same way.
  const pieces = value?.split(CHOICE_PARTS) ?? [];
  const perBlank = blanks > 1 && pieces.length === blanks ? pieces : null;
  let index = 0;
  return parts
    .map((part, i) => {
      if (i % 2 !== 0) return part;
      // A blank touching a letter on either side is a letter missing from a
      // word; one surrounded by spaces is a word missing from a sentence. The
      // neighbours are re-emitted because matching them is the only way to look
      // at them. Detected rather than declared in the content: `{_}` between two
      // word characters is already unambiguous, so this needs no new marker and
      // no migration — it fixes every question already written.
      return part.replace(/(\p{L})?\{_\}(\p{L})?/gu, (_m, before, after) => {
        const inWord = Boolean(before || after);
        let fill = perBlank ? perBlank[index] : value;
        index += 1;
        if (fill && inWord) fill = inWordPiece(fill);
        const slot = blankSpan(inWord, fill);
        return `${before ?? ""}${slot}${after ?? ""}`;
      });
    })
    .join("");
}

/**
 * Component for rendering HTML content with LaTeX math expressions.
 *
 * Uses KaTeX to render math delimited by:
 * - $...$ for inline math
 * - $$...$$ for display (block) math
 *
 * @example
 * <MathContent html="Calculate $\\frac{3}{4}$ of 100" />
 */
export function MathContent({ html, className, style, blankValue, zoomableFigures }: MathContentProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [zoomed, setZoomed] = useState<ZoomedFigure | null>(null);
  const [charts, setCharts] = useState<ChartSlot[]>([]);

  useEffect(() => {
    if (ref.current) {
      // Fix Lithuanian decimal commas: in LaTeX, a bare comma adds thin space
      // (for list separation like f(x, y)). Using {,} suppresses that spacing,
      // so "2,5" renders as a single number, not "2 5".
      const fixedHtml = replaceEmphasis(replaceCodeSpans(html)).replace(
        /\$\$[\s\S]+?\$\$|\$[^$]+?\$/g,
        (math) => math.replace(/(\d),(\d)/g, "$1{,}$2")
      );

      const withPlaceholders = replaceBlankTokens(fixedHtml, blankValue);

      // First set the HTML content. Figures are stamped with the design version
      // on the way in — see lib/figures.ts: they are cached in the browser for a
      // year and the redesign changed no URL.
      ref.current.innerHTML = versionFigures(withPlaceholders);

      // Unwrap <code> tags that only contain math delimiters, e.g. <code>$\frac{1}{2}$</code>
      // The AI generator sometimes wraps math in backticks which become <code> in HTML,
      // and KaTeX auto-render skips <code> elements by default.
      for (const code of Array.from(ref.current.querySelectorAll("code"))) {
        const text = code.textContent || "";
        if (/^\$\$[\s\S]+\$\$$/.test(text) || /^\$[^$]+\$$/.test(text)) {
          code.replaceWith(document.createTextNode(text));
        }
      }

      // Then render any math expressions
      renderMathInElement(ref.current, {
        delimiters: [
          { left: "$$", right: "$$", display: true },
          { left: "$", right: "$", display: false },
        ],
        throwOnError: false,
        errorColor: "#cc0000",
        // Trust HTML content since it's from our own system (needed for \htmlClass)
        trust: true,
        strict: "ignore",
        macros: {
          // Render \square as a "fill-in" placeholder. \phantom keeps width
          // without drawing the ▢ glyph — the styled box itself signals the slot.
          // The surrounding \, thin spaces give breathing room when the box sits
          // between operands (e.g. "5 × 4 ▢ 20") without being noticeable when
          // it follows a relation like "= ▢".
          //
          // **Braced, because an exponent takes exactly one atom.** Unbraced,
          // `3^\square` expanded to `3^\,\htmlClass{...}...` and `^` grabbed the
          // thin space alone — `\,` is `\mskip3mu`, a command needing an argument
          // it could not get, so KaTeX raised a parse error and the *whole*
          // expression rendered as red raw LaTeX. 29 questions read like that,
          // among them "$A = 2^3 \times 3^\square \times 5$". Braces make the
          // placeholder one atom, so it lands in the exponent where it belongs,
          // and change nothing for `= \square` or `4 \square 20`.
          "\\square": "{\\,\\htmlClass{math-blank}{\\phantom{X}}\\,}",
        },
      });

      // Right-align table columns that contain only numeric values
      for (const table of Array.from(ref.current.querySelectorAll("table"))) {
        const rows = Array.from(table.querySelectorAll("tr"));
        if (rows.length < 2) continue;
        const dataRows = rows.slice(1);
        const colCount = rows[0].children.length;
        for (let col = 0; col < colCount; col++) {
          const allNumeric = dataRows.every((row) => {
            const cell = row.children[col] as HTMLElement | undefined;
            const text = cell?.textContent?.trim() || "";
            return /^-?\d[\d\s.,]*\s*(%|€|°C|°|km|kg|g)?$/.test(text) || text === "?";
          });
          if (allNumeric) {
            for (const row of rows) {
              const cell = row.children[col] as HTMLElement | undefined;
              if (cell) cell.style.textAlign = "right";
            }
          }
        }
      }

      // Last, so the badges are measured against images KaTeX and the fixes
      // above have finished moving about.
      if (!zoomableFigures) return;
      // Before the badges, because a chart draws its own: the `<img>` these would
      // have been measured onto is gone by then.
      setCharts(extractCharts(ref.current));
      const placed = markZoomableFigures(ref.current);
      placeBadges(placed);
      // The first measurement is taken before the figures have been fetched, so
      // their boxes are still empty — every one of them has to be measured again
      // once it arrives, and again whenever the column it sits in changes width.
      const remeasure = () => placeBadges(placed);
      for (const [img] of placed) img.addEventListener("load", remeasure);
      const observer = new ResizeObserver(remeasure);
      observer.observe(ref.current);
      return () => {
        observer.disconnect();
        for (const [img] of placed) img.removeEventListener("load", remeasure);
      };
    }
  }, [html, blankValue, zoomableFigures]);

  // Delegated, because the images are written by innerHTML above and are
  // replaced wholesale whenever the question text changes.
  function onFigureActivate(e: React.MouseEvent | React.KeyboardEvent) {
    if (!zoomableFigures) return;
    const img = (e.target as HTMLElement | null)?.closest?.("img.figure-zoomable");
    if (!img) return;
    if ("key" in e && e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    setZoomed(figureFrom(img as HTMLImageElement));
  }

  return (
    <>
      {/* `math-content` is always present, whatever the caller passes: content
          written by the generator needs styles of its own (a quoted passage, a
          table) and there was no selector to hang them on. Callers keep full
          control of layout through `className`. */}
      <div
        ref={ref}
        className={className ? `math-content ${className}` : "math-content"}
        style={style}
        onClick={onFigureActivate}
        onKeyDown={onFigureActivate}
      />
      {/* Drawn into the slots left above. Portals rather than markup because the
          question's HTML is written with `innerHTML` and is replaced wholesale
          whenever the text changes — a React child cannot live inside it. */}
      {charts.map((chart) =>
        createPortal(
          <FigureChart
            src={chart.src}
            alt={chart.alt}
            inline
            onOpen={() => setZoomed({ src: chart.src, alt: chart.alt })}
          />,
          chart.host,
          chart.key
        )
      )}
      {zoomableFigures && <FigureLightbox figure={zoomed} onClose={() => setZoomed(null)} />}
    </>
  );
}
